'use client';

import type { InputHTMLAttributes, ReactNode } from 'react';
import { ClipLoader } from 'react-spinners';

import NextImage from 'next/image';

import { Link } from '../../i18n/navigation';
import BrandLogo from '../BrandLogo';
import Footer from '../Footer';

interface AuthLayoutProps {
  children: ReactNode;
  sideTitle: string;
  sideDescription: string;
  highlights?: string[];
}

interface AuthPanelProps {
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
}

interface AuthInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  error?: string;
  containerClassName?: string;
}

interface AuthSubmitButtonProps {
  label: string;
  loading?: boolean;
  disabled?: boolean;
}

export function AuthLayout({
  children,
  sideTitle,
  sideDescription,
  highlights = []
}: AuthLayoutProps) {
  return (
    <div className="flex min-h-screen flex-col bg-gray-600 text-white">
      <AuthHeader />

      <main className="flex flex-1 items-stretch px-4 py-8 md:px-10 lg:py-12">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 lg:grid-cols-[1fr_440px] lg:items-center">
          <section className="hidden min-h-[620px] overflow-hidden rounded-lg border border-white/10 bg-black lg:flex lg:flex-col lg:justify-between">
            <div className="p-8">
              <p className="text-sm font-semibold uppercase text-red-600">
                Trakio
              </p>
              <h2 className="mt-4 max-w-xl text-4xl font-bold leading-tight text-white">
                {sideTitle}
              </h2>
              <p className="mt-4 max-w-lg text-base leading-7 text-gray-100">
                {sideDescription}
              </p>

              {highlights.length > 0 && (
                <div className="mt-8 grid gap-3">
                  {highlights.map(highlight => (
                    <div
                      key={highlight}
                      className="flex items-center gap-3 text-sm text-gray-100"
                    >
                      <span className="h-2 w-2 rounded-full bg-red-600" />
                      <span>{highlight}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="relative flex min-h-[260px] items-end justify-center overflow-hidden bg-gray-150 px-8 pt-8">
              <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black to-transparent" />
              <NextImage
                src="/assets/landing/mobile-image.png"
                alt=""
                aria-hidden="true"
                className="relative h-auto w-[360px] max-w-full object-contain"
                width={490}
                height={542}
                sizes="360px"
                priority
              />
            </div>
          </section>

          <section className="flex min-h-[calc(100vh-180px)] items-center justify-center lg:min-h-0">
            {children}
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export function AuthHeader() {
  return (
    <header className="flex h-[72px] min-h-[72px] w-full items-center border-b border-white/10 bg-black/95 px-4 md:px-10">
      <Link
        href="/"
        className="flex min-h-[44px] items-center"
        aria-label="Trakio home"
      >
        <BrandLogo priority className="h-[24px] w-[156px]" />
      </Link>
    </header>
  );
}

export function AuthPanel({
  title,
  description,
  children,
  footer
}: AuthPanelProps) {
  return (
    <div className="w-full max-w-[440px] rounded-lg border border-white/10 bg-black p-5 shadow-2xl shadow-black/40 md:p-8">
      <div>
        <h1 className="text-3xl font-bold leading-tight text-white md:text-4xl">
          {title}
        </h1>
        <p className="mt-3 text-sm leading-6 text-gray-100">{description}</p>
      </div>

      <div className="mt-7">{children}</div>

      {footer && (
        <div className="mt-7 border-t border-white/10 pt-5 text-center text-sm text-gray-100">
          {footer}
        </div>
      )}
    </div>
  );
}

export function AuthInput({
  label,
  error,
  containerClassName = '',
  id,
  ...props
}: AuthInputProps) {
  return (
    <div className={`flex flex-col ${containerClassName}`}>
      <label className="text-sm font-semibold text-white" htmlFor={id}>
        {label}
      </label>
      <input
        {...props}
        id={id}
        aria-invalid={Boolean(error)}
        className={`mt-2 h-12 rounded-md border bg-gray-600 px-4 text-base text-white outline-none transition placeholder:text-gray-300 focus:border-red-600 focus:ring-2 focus:ring-red-600/30 ${
          error ? 'border-red-600' : 'border-white/10'
        }`}
      />
      {error && <span className="mt-2 text-sm text-red-600">{error}</span>}
    </div>
  );
}

export function AuthSubmitButton({
  label,
  loading = false,
  disabled = false
}: AuthSubmitButtonProps) {
  return (
    <button
      className="flex h-12 w-full items-center justify-center rounded-md bg-red-600 px-4 text-base font-bold text-white transition hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-600/50 focus:ring-offset-2 focus:ring-offset-black disabled:cursor-not-allowed disabled:opacity-70"
      type="submit"
      disabled={disabled || loading}
    >
      {loading ? (
        <ClipLoader color="#fff" loading={loading} size={18} />
      ) : (
        label
      )}
    </button>
  );
}

export function AuthDivider() {
  return (
    <div className="my-6 flex items-center gap-4">
      <span className="h-px flex-1 bg-white/10" />
      <span className="h-px flex-1 bg-white/10" />
    </div>
  );
}
